import useLocalState from '@phntms/use-local-state';
import { camelCase, debounce, kebabCase, upperFirst } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useMatch, useParams } from 'react-router-dom';

import { PatientList } from '../../compositions/patients/list/patient-list';
import { PatientCreateModal } from '../../compositions/patients/modals/patient-create-modal/patient-create-modal';
import { PatientModal } from '../../compositions/patients/modals/patient-modal/patient-modal';
import { useAppContext } from '../../data/context/app-context';
import { usePatientGroupItems } from '../../data/data/patients/use-patient-group-items';
import { usePatientItems } from '../../data/data/patients/use-patient-items';
import { useOpenRegistrationCount } from '../../data/data/registrations/use-open-registration-count';
import {
  PatientGroup,
  PatientGroupItemDataFragment,
  PatientItemDataFragment,
} from '../../data/types/api/graphql';
import { useNavigateWithHealthProvider } from '../../data/utils/router.utils';
import { Button, ButtonIcon } from '../../elements/button/button';
import { Content } from '../../elements/content/content';
import { ContentMessage } from '../../elements/content/content-message';
import { BaseInputIcon } from '../../elements/form/input/base-input';
import { InputField } from '../../elements/form/input/input-field';
import { CustomFormattedMessage } from '../../elements/intl/custom-formatted-message';
import { Sidebar } from '../../elements/sidebar/sidebar';
import { SidebarList } from '../../elements/sidebar-list/sidebar-list';
import { SidebarListItem } from '../../elements/sidebar-list/sidebar-list-item';
import {
  DataTableLocationState,
  DataTablePageSize,
} from '../../elements/table/data-table/data-table';
import {
  Pagination,
  PaginationData,
} from '../../elements/table/pagination/pagination';
import { RegistrationsPage } from '../registrations/registrations';

export function PatientsPage() {
  const intl = useIntl();

  const { healthProviderId } = useAppContext();

  const { group } = useParams();

  // Matches the detail route too, so the entry stays active with a modal open.
  const isRegistrations = !!useMatch(
    '/:wildcard/patients/registrations/:registrationId?'
  );

  const { data: openRegistrationCountData } =
    useOpenRegistrationCount(healthProviderId);

  const [patientModalOpen, setPatientModalOpen] = useState<
    PatientItemDataFragment | false
  >(false);

  const [patientCreateModalOpen, setPatientCreateModalOpen] =
    useState<boolean>(false);

  const { data: patientGroupItemsData, loading: patientGroupItemsLoading } =
    usePatientGroupItems(healthProviderId);

  const location = useLocation();

  const navigate = useNavigateWithHealthProvider();

  const [limit, setLimit] = useLocalState<number>(
    'dtPatientsLimit',
    DataTablePageSize
  );

  const [paging, setPaging] = useState<PaginationData>({
    page: (location?.state as DataTableLocationState)?.page ?? 1,
    total: 0,
  });

  const [query, setQuery] = useState<string>(
    (location?.state as DataTableLocationState)?.query ?? ''
  );

  const { data: patientItemsData, loading: patientItemsLoading } =
    usePatientItems({
      healthProviderId,
      group: upperFirst(camelCase(group)) as PatientGroup,
      take: limit,
      skip: (paging.page - 1) * limit,
      search: query,
    });

  const patientItems = useMemo(() => {
    const total = patientItemsData?.totalCount ?? 0;

    if (patientItemsData) {
      setPaging(prevState => ({
        ...prevState,
        total,
      }));
    }

    return patientItemsData?.items ?? [];
  }, [patientItemsData]);

  useEffect(() => {
    if (query.length > 0) {
      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    }
  }, [query]);

  const onHandlePatientGroup = useCallback(
    (value: PatientGroupItemDataFragment) => {
      navigate(`/patients/${kebabCase(value.key)}`);
    },
    [navigate]
  );

  const onHandleSearch = useCallback((value: string) => {
    setQuery(value);
  }, []);

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const onHandleLimit = useCallback(
    (value: number) => {
      setLimit(value);

      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    },
    [setLimit]
  );

  const onHandlePagination = useCallback((value: number) => {
    setPaging(prevState => ({
      ...prevState,
      page: value,
    }));
  }, []);

  const onHandlePatient = useCallback((value: PatientItemDataFragment) => {
    setPatientModalOpen(value as PatientItemDataFragment);
  }, []);

  useEffect(() => {
    setPatientModalOpen(false);

    setPaging(prevState => ({
      ...prevState,
      page: 1,
    }));
  }, [healthProviderId, group]);

  return (
    <>
      <Sidebar
        title={<CustomFormattedMessage id="patients.title" />}
        toolbar={
          <Button
            icon={ButtonIcon.USER_ADD}
            onClick={() => setPatientCreateModalOpen(true)}
          >
            <CustomFormattedMessage id="patients.button.create-patient" />
          </Button>
        }
      >
        <SidebarList
          loading={patientGroupItemsLoading}
          items={[
            ...patientGroupItemsData.map(option => (
              <SidebarListItem
                key={option.key}
                title={
                  <CustomFormattedMessage
                    id={`patients.group.${kebabCase(option.key)}`}
                  />
                }
                values={{
                  value: option.value,
                }}
                active={group === kebabCase(option.key)}
                onClick={() => onHandlePatientGroup(option)}
              />
            )),
            <SidebarListItem
              key="registrations"
              testId="patients-registrations"
              title={
                <CustomFormattedMessage id="patients.group.registrations" />
              }
              // The open count is the whole point of the entry: a registration
              // waits on a person, so the practice has to see there is work.
              values={{ value: openRegistrationCountData || null }}
              active={isRegistrations}
              onClick={() => navigate('/patients/registrations')}
            />,
          ]}
        />
      </Sidebar>

      {isRegistrations ? (
        <RegistrationsPage />
      ) : (
        <Content
          toolbar={
            group ? (
              <InputField
                icon={BaseInputIcon.SEARCH}
                type="search"
                name="search"
                placeholder={intl.formatMessage({
                  id: 'common.patients-search-placeholders',
                })}
                onChange={value => onHandleSearchDebounce(value)}
              />
            ) : undefined
          }
        >
          {!group && (
            <ContentMessage
              message={<CustomFormattedMessage id="patients.select-group" />}
            />
          )}

          {group && (
            <>
              <PatientList
                loading={patientItemsLoading}
                items={patientItems}
                onClick={onHandlePatient}
              />

              <Pagination
                limit={limit}
                paging={paging}
                onChangeLimit={onHandleLimit}
                onChangePage={onHandlePagination}
              />
            </>
          )}
        </Content>
      )}

      {patientModalOpen && (
        <PatientModal
          healthProviderId={healthProviderId}
          patientId={patientModalOpen.id}
          onClose={() => {
            setPatientModalOpen(false);
          }}
        />
      )}

      {patientCreateModalOpen && (
        <PatientCreateModal
          healthProviderId={healthProviderId}
          onClose={() => {
            setPatientCreateModalOpen(false);
          }}
        />
      )}
    </>
  );
}
